/**
 * The cache lifecycle around one drag: the mutation, the optimistic placement it publishes,
 * the confirmed placement it settles, and the two ways a write can come back unapplied.
 *
 * It names no endpoint and builds no URL. The transport is
 * `SavedViewsBoardAdapter.moveBoardItem`, which is what lets a second board reuse all of
 * this by supplying a different one.
 *
 * The overlay lives in the mutation cache rather than the query cache, so a pending move
 * survives the board being re-keyed by a fresh route response: `useOptimisticData` applies
 * it over whatever base the query currently holds.
 */

import {announce} from '@github-ui/aria-live'
import type {BoardItemDragState, BoardItemsMove, CellAddress} from '@github-ui/board-core'
import {entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import type {EntityMutationVariables} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {type MutationFilters, useMutationState} from '@github-ui/react-query'
import {useCallback, useMemo, useState} from 'react'

import {heldCells} from './board-document-adapter'
import {BoardMoveFailedError, BoardMoveRefusedError} from './board-move-errors'
import {readBoardPlacement, toConfirmedPlacementOperation, toOptimisticPlacementOperation} from './board-placement'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardMoveResult,
  SavedViewsBoardOptions,
} from './types'

const CANNOT_MOVE_ITEM = 'You do not have permission to move this item.'
const ALREADY_MOVING = 'Wait for this item’s last move to finish before moving it again.'
const MOVE_FAILED = 'Could not move this item. Please try again.'
const LEFT_THE_VIEW = 'Moved. This item no longer matches this view and will not appear on the next refresh.'

export type UseBoardItemMoveResult = {
  /**
   * Handed to the board only on a board that moves cards at all. Absent is what makes a
   * board read-only: no card can be picked up and no cell is a drop target.
   */
  onItemsMove?: (move: BoardItemsMove) => void
  getItemDragState: (item: SavedViewsBoardItem) => BoardItemDragState
  /**
   * Why a card can never be moved on this board, for the card to write out. Leaves out a
   * move in flight, which `getItemDragState` still refuses: it clears within moments, and
   * text added and removed on every move is noise.
   */
  getMoveRefusal: (item: SavedViewsBoardItem) => string | undefined
  canDropItem: (item: SavedViewsBoardItem, move: {from: CellAddress; to: CellAddress}) => boolean
  /** What the last write said when it did not apply, for the host to draw. */
  moveError?: string
  clearMoveError: () => void
}

export function useBoardItemMove<TItem extends SavedViewsBoardItem>({
  adapter,
  options,
  boardData,
  onBoardWrite,
}: {
  adapter: Pick<SavedViewsBoardAdapter<TItem>, 'moveBoardItem'>
  options: SavedViewsBoardOptions
  boardData: SavedViewsBoardData<TItem>
  /**
   * Called twice for one move: a read from before the gesture and one from during the write
   * are both stale, and the two windows do not overlap. Not called when a write fails.
   */
  onBoardWrite?: () => void
}): UseBoardItemMoveResult {
  const [moveError, setMoveError] = useState<string | undefined>(undefined)
  /**
   * The cards with a write in flight.
   *
   * A second move of the same card while the first is still going is the one case the
   * overlay cannot recover from: two writes to one field settle in whatever order the
   * responses arrive, the later fold wins the cache, and this board does not refetch, so a
   * board left on the earlier value stays there. Refusing the second gesture keeps that from
   * happening.
   */
  const movingItemIds = usePendingBoardMoves()
  const {columnField, rowField, viewerCanMoveBetweenColumns, viewerCanMoveBetweenRows} = boardData
  const {moveBoardItem} = adapter

  const report = useCallback((message: string) => {
    setMoveError(message)
    announce(message)
  }, [])

  const {mutate} = useEntityMutation<SavedViewsBoardMoveResult>({
    mutationFn: async operation => {
      const gesture = readBoardPlacement(operation)?.gesture
      if (moveBoardItem === undefined || gesture === undefined || columnField == null) {
        throw new BoardMoveFailedError()
      }

      return moveBoardItem({
        itemId: operation.id,
        from: gesture.from,
        to: gesture.to,
        arrangement: {columnField, rowField},
        query: options.query,
      })
    },
    // Built from the write rather than from the drag, so a server that normalised the value,
    // clipped it or left it alone is what the board settles to. A response naming another
    // card folds onto nothing, so it settles nothing either.
    getConfirmedOperation: (result, operation) =>
      result.itemId === operation.id ? toConfirmedPlacementOperation(result) : undefined,
    onSuccess: result => {
      onBoardWrite?.()
      if (result.changedFieldMatchesView) return
      // The card's new value contradicts the view's own search, so it has left the board.
      // Saying so is the honest minimum: the card stays where the record now puts it, and
      // the next board read drops it. The departure treatment is github/views#84.
      report(LEFT_THE_VIEW)
    },
    onError: error => {
      // A refusal is shown in the server's own words and a failure in ours. The overlay
      // drops either way, and it drops whole: one gesture is one operation, so a refused
      // diagonal move takes both halves with it rather than leaving the card in a cell that
      // is neither where it was nor where it was dropped.
      report(error instanceof BoardMoveRefusedError ? error.message : MOVE_FAILED)
    },
  })

  const canMoveItems =
    (viewerCanMoveBetweenColumns === true || viewerCanMoveBetweenRows === true) &&
    moveBoardItem !== undefined &&
    columnField != null

  const onItemsMove = useCallback(
    (move: BoardItemsMove) => {
      // A drop carries exactly one card. Moving a selection is not offered, and applying
      // part of a list nobody can produce would be worse than refusing it.
      const [itemId] = move.itemIds
      if (itemId === undefined || move.itemIds.length !== 1 || columnField == null) return
      if (movingItemIds.has(itemId)) return

      setMoveError(undefined)
      onBoardWrite?.()
      mutate({
        operation: toOptimisticPlacementOperation({
          itemId,
          from: move.from,
          to: move.to,
          arrangement: {columnField, rowField},
          // What the card holds on an axis the gesture did not cross has to survive the
          // move, and the board is the only thing that knows it.
          held: heldCells(boardData, itemId),
        }),
      })
    },
    [boardData, columnField, movingItemIds, rowField, mutate, onBoardWrite],
  )

  const getItemDragState = useCallback(
    (item: SavedViewsBoardItem): BoardItemDragState => {
      const refusal = getMoveRefusal(item)
      if (refusal !== undefined) return {draggable: false, reason: refusal}
      if (movingItemIds.has(item.globalId)) return {draggable: false, reason: ALREADY_MOVING}

      return {draggable: true}
    },
    [movingItemIds],
  )

  const clearMoveError = useCallback(() => setMoveError(undefined), [])

  return {
    onItemsMove: canMoveItems ? onItemsMove : undefined,
    getItemDragState,
    getMoveRefusal,
    canDropItem,
    moveError,
    clearMoveError,
  }
}

function getMoveRefusal(item: SavedViewsBoardItem): string | undefined {
  return item.canMoveBetweenColumns === true || item.canMoveBetweenRows === true ? undefined : CANNOT_MOVE_ITEM
}

/**
 * A drop that crosses an axis the card cannot be written on is refused before it is made. A
 * card in several rows dragged into another of them still crosses rows: the write would
 * change the row field, and the server refuses that.
 */
function canDropItem(item: SavedViewsBoardItem, {from, to}: {from: CellAddress; to: CellAddress}): boolean {
  return (
    (from.columnKey === to.columnKey || item.canMoveBetweenColumns === true) &&
    (from.rowKey === to.rowKey || item.canMoveBetweenRows === true)
  )
}

/** Every board move in the mutation cache, made from any board on the page. */
export const BOARD_MOVES: MutationFilters = {
  mutationKey: entityFieldsMutationKey(),
  predicate: mutation => isBoardMove(mutation.state.variables),
}

/**
 * The ids of every card with a board move pending anywhere on the page.
 *
 * Read from the mutation cache rather than held in this board, because the overlay lives
 * there too: a board that remounts, or a second board showing the same card, has to refuse
 * the card for as long as the overlay holds it. A move leaves `pending` only after
 * `useEntityMutation` folds the write into the cache.
 */
function usePendingBoardMoves(): ReadonlySet<string> {
  const itemIds = useMutationState<string>({
    // Filtered here rather than in `select`, so a field edit elsewhere on the page leaves the
    // result identical and redraws no card on this board.
    filters: {...BOARD_MOVES, status: 'pending'},
    select: mutation => (mutation.state.variables as EntityMutationVariables).operation.id,
  })

  return useMemo(() => new Set(itemIds), [itemIds])
}

function isBoardMove(variables: unknown): variables is EntityMutationVariables {
  const operation = (variables as EntityMutationVariables | undefined)?.operation
  return operation !== undefined && readBoardPlacement(operation) !== undefined
}
